import * as z from 'zod'

import { AggregatorOccupation } from '~/shared/api/aggregatorOccupation'
import { Taxipark } from '~/shared/api/taxipark'
import { InfoSourceEnum, TaxSystemEnum } from '~/shared/config/enums'
import emailSchema, {
  dateSchema,
  enumOptionSchema,
  innRequiredSchema,
  phoneScheme,
  pickImagesRequiredSchema,
  snilsScheme,
  stringRequiredScheme,
  uuidOptionSchema,
} from '~/shared/lib/schemas'
import { ApiModel, ToOneRelation } from './core'

const relationsSchema = z.object({
  aggregatorOccupationOption: uuidOptionSchema,
})

const attributeSchema = z.object({
  lastName: stringRequiredScheme,
  firstName: stringRequiredScheme,
  middleName: z.string().optional().nullable(),
  phone: phoneScheme,
  birthDate: dateSchema,
  birthPlace: stringRequiredScheme,
  passportIssueDate: dateSchema,
  citizenship: uuidOptionSchema,
  registrationAddress: stringRequiredScheme,
  passportNumber: stringRequiredScheme,
  passportIssued: stringRequiredScheme,
  divisionCode: stringRequiredScheme,
  drivingLicenseIssuingCountry: uuidOptionSchema,
  drivingLicenseNumber: stringRequiredScheme,
  drivingLicenseIssueDate: dateSchema,
  drivingLicenseExpirationDate: dateSchema,
  drivingExperienceStartDate: dateSchema,
  placeOfResidence: stringRequiredScheme,
  snils: snilsScheme,
  email: emailSchema,
  photo: pickImagesRequiredSchema.max(1).transform((values) => {
    return values[0]
  }),
  passportPhotos: pickImagesRequiredSchema.refine(
    (images) => images.length >= 2,
    {
      message: 'Добавьте фотографию паспорта основного разворота и прописки',
    },
  ),
  drivingLicensePhotos: pickImagesRequiredSchema.refine(
    (images) => images.length >= 2,
    {
      message: 'Добавьте фотографии водительских прав с двух сторон',
    },
  ),
  regionCode: uuidOptionSchema,
  infoSource: enumOptionSchema(InfoSourceEnum, 'Выберите источник информации')
    .array()
    .min(1, 'Выберите хотя бы один источник информации'),
  inn: innRequiredSchema.optional().nullable(),
  taxSystem: enumOptionSchema(TaxSystemEnum).optional(),
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributeSchema)
  .merge(relationsSchema)

export type DriverRegistrationRequestAttributes = z.infer<
  typeof attributeSchema
>

export class DriverRegistrationRequest extends ApiModel<
  typeof schema,
  DriverRegistrationRequestAttributes
> {
  static jsonApiType = 'driver-registration-requests'

  static schema = schema

  taxipark(): ToOneRelation<Taxipark, this> {
    return this.hasOne(Taxipark)
  }
  getTaxipark(): Taxipark {
    return this.getRelation('taxipark')
  }
  setTaxipark(id: UniqueId) {
    const taxipark = new Taxipark()
    taxipark.setApiId(id)
    this.setRelation('taxipark', taxipark)
  }

  aggregatorOccupation(): ToOneRelation<AggregatorOccupation, this> {
    return this.hasOne(AggregatorOccupation)
  }
  getAggregatorOccupation(): AggregatorOccupation {
    return this.getRelation('aggregatorOccupation')
  }
  setAggregatorOccupation(id: UniqueId) {
    const aggregatorOccupation = new AggregatorOccupation()
    aggregatorOccupation.setApiId(id)
    this.setRelation('aggregatorOccupation', aggregatorOccupation)
  }

  static async uploadMedia(
    file: Blob,
    taxiparkId?: UniqueId,
  ): Promise<UniqueId> {
    const url = `${DriverRegistrationRequest.getJsonApiUrl()}/actions/upload-photo`

    const formData = new FormData()
    formData.append('file', file)
    formData.append('type', 'registrationDriver')

    if (taxiparkId) {
      formData.append('taxiparkId', taxiparkId)
    }

    const client = DriverRegistrationRequest.httpClient.getImplementingClient()
    const { data } = await client.post<{ uuid: UniqueId }>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })

    return data.uuid
  }

  static async sendCode(phone: string) {
    const url = `${DriverRegistrationRequest.getJsonApiUrl()}/actions/send-code`
    const client = DriverRegistrationRequest.httpClient.getImplementingClient()
    return await client.post(url, { phone })
  }

  async validate() {
    const url = `${DriverRegistrationRequest.getJsonApiUrl()}/actions/validate`
    const client = DriverRegistrationRequest.httpClient.getImplementingClient()
    return await client.post(url, this.getJsonApiData())
  }
}
